import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import Background from '../shared/Background/Background';
import Container from './components/Container';
import Header from './components/Header';
import Description from './components/Description';
import ActionsContainer from './components/ActionsContainer';
import InputContainer from './components/InputContainer';
import InWikiEmailButton from './components/InWikiEmailButton';
import IconEnvelope from '@fandom-frontend/react-common/dist/icons/IconEnvelope';
import Wrapper from '../shared/Wrapper/Wrapper';
import MessageContainer from './components/MessageContainer';
import Highlight from './components/Highlight';
import HeaderContainer from './components/HeaderContainer';
import FeedbackMessage from './components/FeedbackMessage';
import FadeIn from './components/FadeIn';
import { validateEmail } from '../../utils/validateEmail';
import { subscribeToMailingList } from '../../utils/marketingMailingUtils';
import useTrackImpression from 'fandom-frontend-ready/tracking/useTrackImpression';
import styles from 'pathfinder/modules/InWikiEmailModule/InWikiEmailModule.module.scss';
export default function InWikiEmailModule({ config, track }) {
    var _a, _b;
    const { ref, onScreen } = useTrackImpression();
    const [email, setEmail] = useState('');
    // Keeps track of whether the subscription request is still occurring,
    // but signals that a valid email was provided. A value of "true" does not necessarily
    // mean that the request was successful, but it definitely does mean that the request
    // was at least send out to the backend service for processing.
    // This value should only change back from true to false if the request errors out.
    const [emailSent, setEmailSent] = useState(false);
    const [emailError, setEmailError] = useState(null);
    // Keeps track of whether the users entered a valid email in the email input box.
    const [isValidEmail, setIsValidEmail] = useState(false);
    // Keeps track of whether there were any errors that came up during sign up.
    const [signUpError, setSignUpError] = useState(null);
    // Keeps track of whether the request fully resolved successfully
    const [signUpSuccessful, setSignUpSuccessful] = useState(null);
    useEffect(() => {
        if (onScreen) {
            track.impression();
        }
    }, [onScreen]);
    const validateEmailAttempt = () => {
        return validateEmail(email);
    };
    const handleChange = (event) => {
        const value = event.currentTarget.value;
        setEmail(value);
    };
    const emailValidationSuccess = () => {
        setEmailError(null);
        setIsValidEmail(true);
        setEmailSent(true);
    };
    const onUserSubscribedSuccessfully = () => __awaiter(this, void 0, void 0, function* () {
        setSignUpSuccessful(true);
    });
    const onUserSubscribeFailure = () => __awaiter(this, void 0, void 0, function* () {
        // Show a sign-up error message
        setSignUpError(true);
        // Clear the rest of the email states,
        // so that the user can resubmit their email.
        setEmailSent(false);
        setEmailError(null);
        setIsValidEmail(null);
    });
    const emailValidationFailure = () => {
        setEmailError('Please enter valid email address.');
        setIsValidEmail(false);
    };
    const onSubmitEmail = () => __awaiter(this, void 0, void 0, function* () {
        // In case there was a sign-up error previously,
        // let's clear it so that the sign-up error message clears.
        setSignUpError(false);
        const isValidEmail = validateEmailAttempt();
        if (isValidEmail) {
            emailValidationSuccess();
            // TODO: Should we re-evaluate tracking in some way? Or should these events stay the same?
            track({ action: 'email_entry', email });
            subscribeToMailingList(email, onUserSubscribedSuccessfully, onUserSubscribeFailure);
        }
        else {
            emailValidationFailure();
        }
    });
    const title = (_a = config.title) !== null && _a !== void 0 ? _a : 'Stay up to date on your Fandom';
    const subtitle = (_b = config.subtitle) !== null && _b !== void 0 ? _b : 'Stay up to date on your fandom with carefully curated and timely Fandom newsletters.';
    const inputClassName = clsx(styles.input, {
        [styles.inputError]: !!emailError,
    });
    return (_jsx(Wrapper, Object.assign({ className: styles.wrapper, isVisible: true, isStatic: true, ref: ref }, { children: _jsx(Background, Object.assign({ centerItems: true, isDark: false }, { children: _jsxs(Container, Object.assign({ dataTestId: "InWikiEmailContent" }, { children: [_jsxs(HeaderContainer, { children: [_jsx(IconEnvelope, { className: styles.iconEnvelope }, void 0), _jsx(Highlight, { children: _jsx(Header, { children: title }, void 0) }, void 0)] }, void 0), _jsx(Description, { children: subtitle }, void 0), _jsxs(ActionsContainer, { children: [_jsx(InputContainer, { children: _jsx("input", { className: inputClassName, "data-testid": "InWikiEmailInput", disabled: emailSent, onChange: handleChange, type: "email", placeholder: "Email Address" }, void 0) }, void 0), _jsx(InWikiEmailButton, Object.assign({ "data-testid": "InWikiEmailButton", disabled: emailSent, onClick: onSubmitEmail }, { children: emailSent ? 'Thanks!' : 'Sign up' }), void 0)] }, void 0), _jsxs(MessageContainer, Object.assign({ centerText: !emailError && isValidEmail }, { children: [emailError && (_jsx(FadeIn, { children: _jsx(FeedbackMessage, Object.assign({ error: true }, { children: "Please enter valid email address." }), void 0) }, void 0)), signUpError && (_jsx(FadeIn, { children: _jsx(FeedbackMessage, Object.assign({ error: true }, { children: "There was an issue during sign-up." }), void 0) }, void 0)), !emailError && isValidEmail && signUpSuccessful && (_jsx(FadeIn, { children: _jsx(FeedbackMessage, { children: "Thanks for signing up!" }, void 0) }, void 0))] }), void 0)] }), void 0) }), void 0) }), void 0));
}
